import React, { useContext, useState } from "react"
import { ThemeContext } from "styled-components"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import { compareDesc, compareAsc } from "date-fns"

import { parseLocalDate, toLocalMidnightString } from "../../utils/date"
import Reveal from "../utils/Reveal"
import useBreakpoint from "../../hooks/use-breakpoint"

import Section from "../atoms/Section"
import FilteredGrid from "../molecules/FilteredGrid"
import Post from "../molecules/Post"
import SearchGrid from "../molecules/SearchGrid"
import SectionTitle from "../molecules/SectionTitle"

const defaultCard = {
  borderRadius: "md",
  boxShadow: "md",
  bg: 0,
  theme: "default",
  useCard: true,
}

const BlogGrid = ({
  searchGrid,
  BGSearchGrid,
  useSearchFilter,
  section,
  sectionTitle = {},
  flexGrid,
  posts = [],
  card,
  showDate,
  filter,
  BGFilter,
  filters,
  BGFilters,
  sort,
  clearFiltersBtn,
  BGclearFiltersBtn,
  alignText,
  BGAlignText,
  zoomOnHover,
  underlineOnHover,
  displayImages = true,
  postTitleVariant = "h3",
}) => {
  clearFiltersBtn = {
    ...(clearFiltersBtn || BGclearFiltersBtn),
    clearFiltersBtnText:
      clearFiltersBtn?.clearFiltersBtnText?.length > 0
        ? clearFiltersBtn?.clearFiltersBtnText
        : "Clear Filters",
  }

  showDate = showDate === null ? true : showDate // setting default value for older sites
  card = card === null ? defaultCard : card // setting default value for older sites
  filter = BGFilter || filter
  filters = BGFilters || filters
  searchGrid = { ...(BGSearchGrid || searchGrid), useSearch: useSearchFilter }

  const searchProps = {
    ...searchGrid,
    useSearch: useSearchFilter,
    optionOneBoolField: "title",
    optionTwoBoolField: "description",
  }

  const isSearchFiltersOn = searchProps?.useSearch

  const isTagFilterOn = filters?.length > 0
  const shouldRenderClearFiltersButton =
    clearFiltersBtn.showClearFiltersBtn && (isTagFilterOn || isSearchFiltersOn)

  const resolvedTextAlign = BGAlignText || alignText || sectionTitle.align
  const center = resolvedTextAlign === "center"
  const { columns, itemsDisplayedDesktop, itemsDisplayedMobile } = flexGrid

  const currentTheme = useContext(ThemeContext)
  const [searchTerm, setSearchTerm] = useState("")
  const [filtersResetKey, setFiltersResetKey] = useState(0) // used to create unique key to force a remount of filter tags to ensure selected filters are reset when the clear button is clicked

  // Logic to calculate how many items to load before Load More button
  const breakpointsOrdered = [
    { _: false },
    { xs: useBreakpoint("xs") },
    { sm: useBreakpoint("sm") },
    { md: useBreakpoint("md") },
    { lg: useBreakpoint("lg") },
    { xl: useBreakpoint("xl") },
  ]
  const columnsBpOrdered = breakpointsOrdered.filter(bp =>
    Object.keys(columns).includes(Object.keys(bp)[0]),
  )

  let currentColumns
  for (let i = 0; i < columnsBpOrdered.length; i++) {
    // figure out current number of columns using breakpoint
    const isBelowBp = Object.values(columnsBpOrdered[i])[0]
    const prevIdx = i >= 1 ? i - 1 : 0
    const lastIdx = columnsBpOrdered.length - 1
    if (isBelowBp) {
      const currentBp = Object.keys(columnsBpOrdered[prevIdx])[0]
      currentColumns = columns[currentBp]
      break
    } else {
      const currentBp = Object.keys(columnsBpOrdered[lastIdx])[0]
      currentColumns = columns[currentBp]
    }
  }

  let numToLoad
  const isMobileBp = breakpointsOrdered[3]["md"]
  if (isMobileBp && itemsDisplayedMobile) {
    numToLoad = itemsDisplayedMobile
  } else if (itemsDisplayedDesktop) {
    numToLoad = itemsDisplayedDesktop
  } else {
    // if itemsDisplayed values are null (i.e. old sections)
    // assign recommended numToLoad based on number of columns
    switch (currentColumns) {
      case 1:
      case 2:
        numToLoad = 6
        break
      case 3:
        numToLoad = 9
        break
      case 4:
        numToLoad = 16
        break
      case 5:
        numToLoad = 30
        break
      default:
        numToLoad = 6
    }
  }

  const handleSearch = e => {
    const searchValue = e.target.value
    setSearchTerm(searchValue)
  }

  const handleClearFilters = () => {
    setSearchTerm("")
    setFiltersResetKey(filtersResetKey + 1) // increment filtersResetKey with every clear reset to ensure unique filter for each filter tag instance
  }

  // Sort by *calendar day* so ordering is timezone-free
  const sortedPosts =
    posts?.slice().sort((a, b) => {
      const da = parseLocalDate(a?.date)
      const db = parseLocalDate(b?.date)
      if (!da || !db) return 0
      return sort === "newest"
        ? compareDesc(da, db)
        : sort === "oldest"
          ? compareAsc(da, db)
          : 0
    }) ?? []

  return (
    <Section {...section}>
      {sectionTitle && (
        <Reveal>
          <SectionTitle {...sectionTitle} mb={6} maxWidth={42} />
        </Reveal>
      )}

      {searchGrid?.useSearch && (
        <SearchGrid
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          alignment={searchGrid?.alignSearchBar}
          placeholder={searchGrid?.defaultSearchBarText || "Search"}
          themeColors={{
            themes: currentTheme?.themes,
            sectionTheme: section?.theme,
          }}
        />
      )}

      <FilteredGrid
        key={filtersResetKey}
        filtersResetKey={filtersResetKey}
        flexGrid={flexGrid}
        filters={filters}
        filtersStyles={{
          filterLocation: filter?.filterLocation,
          alignMenuItems: filter?.alignMenuItems,
          showFilterLabel: filter?.showFilterLabel,
          layout: filter?.layout,
          filterTextDisplay: filter?.filterTextDisplay,
          dropdownCard: filter?.dropdownCard,
          mb: 0,
        }} // only sending style fields to avoid possibly overwriting filter data
        card={{
          theme: section?.theme,
          bg: section?.bg,
          borderRadius: "md",
          boxShadow: "0 3px 10px rgb(0 0 0 / 30%)",
        }}
        searchedTerm={searchTerm}
        searchProps={searchProps}
        clearFiltersBtn={{
          ...clearFiltersBtn,
          showClearFiltersBtn: shouldRenderClearFiltersButton,
          handleClearFilters,
        }}
        loadMoreBtnStyles={{
          alignItems: center ? "center" : "flex-start",
        }}
        numToLoad={numToLoad}
        currentColumns={currentColumns}
      >
        {sortedPosts.map((post, i) => {
          // Provide a local-midnight string so any `new Date(date)` stays stable
          const parsed = parseLocalDate(post?.date)
          const safeDate = parsed ? toLocalMidnightString(parsed) : post?.date

          return (
            <Post
              {...post}
              key={i}
              date={safeDate}
              showDate={showDate}
              align={resolvedTextAlign}
              card={card}
              imageFieldId={`${section.sectionId}.posts[${i}].thumbnail`}
              zoomOnHover={zoomOnHover}
              underlineOnHover={underlineOnHover}
              displayImages={
                displayImages === null || displayImages === undefined
                  ? true
                  : displayImages
              }
              postTitleVariant={postTitleVariant}
            />
          )
        })}
      </FilteredGrid>
    </Section>
  )
}

export default BlogGrid

BlogGrid.strapiProps = {
  strapi_id: PropTypes.number.isRequired,
  section: PropTypes.shape(Section.strapiProps).isRequired,
  sectionTitle: PropTypes.shape(SectionTitle.strapiProps).isRequired,
  posts: PropTypes.arrayOf(PropTypes.shape(Post.strapiProps)),
  sort: PropTypes.oneOf(["newest", "oldest", "manual"]),
  alignText: PropTypes.oneOf(["left", "center", "right"]),
  ...FilteredGrid.strapiProps,
}

BlogGrid.propTypes = {
  ...BlogGrid.strapiProps,
}

export const query = graphql`
  fragment BlogGrid on STRAPI__COMPONENT_SECTIONS_BLOG_GRID {
    strapi_id
    BLOGGRIDSection: section {
      ...Section
    }
    sectionTitle {
      ...SectionTitle
    }
    flexGrid {
      ...FlexGrid
    }
    posts {
      ...Post
    }
    card {
      ...Card
    }
    BGFilters: filters {
      ...Filter
    }
    BGFilter: filter {
      alignMenuItems
      filterLocation
      showFilterLabel
      layout
      filterTextDisplay
      dropdownCard {
        ...Card
      }
    }
    showDate
    sort
    useSearchFilter
    BGclearFiltersBtn: clearFiltersBtn {
      ...ClearFiltersButton
    }
    BGSearchGrid: searchGrid {
      ...SearchGrid
    }
    BGAlignText: alignText
    zoomOnHover
    underlineOnHover
    displayImages
    postTitleVariant
  }
`
